import cloneDeep from 'lodash/cloneDeep';
import { EditorEvent } from '../constants';
import { Command, Graph } from '../types';
import { getGraphState } from '../utils';

export class CommandManager {
  command: {
    [propName: string]: Command;
  };
  commandQueue: Command[];
  commandIndex: number;

  constructor() {
    this.command = {};
    this.commandQueue = [];
    this.commandIndex = 0;
  }

  /** Register command */
  register(name: string, command: Command) {
    this.command[name] = {
      ...command,
      name,
    };
  }

  /** Execute a command */
  execute(graph: Graph | undefined, name: string, params?: Record<string, unknown>) {
    if (!graph) return;

    const currentCommand = this.command[name];

    if (!currentCommand) return;

    const command = Object.create(currentCommand);

    command.params = cloneDeep(currentCommand.params);

    if (params) {
      command.params = {
        ...command.params,
        ...params,
      };
    }

    if (!command.canExecute(graph)) return;
    if (!command.shouldExecute(graph)) return;

    command.init(graph);

    graph.emit(EditorEvent.onBeforeExecuteCommand, {
      name: command.name,
      params: command.params,
    });

    command.execute(graph);

    graph.emit(EditorEvent.onAfterExecuteCommand, {
      name: command.name,
      params: command.params,
    });

    if (command.canUndo(graph)) {
      const { commandQueue, commandIndex } = this;

      commandQueue.splice(commandIndex, commandQueue.length - commandIndex, command);

      this.commandIndex += 1;
    }

    graph.emit(EditorEvent.onGraphStateChange, {
      graphState: getGraphState(graph),
    });
  }

  /** Determine if the command can be executed */
  canExecute(graph: Graph | null, name: string) {
    if (!graph) return false;

    return this.command[name].canExecute(graph);
  }

  /** Whether the injection should be executed */
  injectShouldExecute(name: string, shouldExecute: (graph: Graph) => boolean) {
    this.command[name].shouldExecute = shouldExecute;
  }
}

export default CommandManager;
